import { useRuntimeConfig, useRoute } from '#imports';
import { useSchemaOrgHead } from '~/composables/useSchemaOrgHead';
import type { BlockAuthor, PageBlocks, PageTemplate } from '~/types/pages';
import {
  absoluteUrl,
  collectionItemType,
  organizationId,
  resolveSchemaKind,
  webpageId,
  webpageTypeByKind,
  websiteId,
  type SchemaKind,
  type SchemaOrgNode,
} from '~/utils/schemaOrg';

interface ListItemInput {
  url?: string;
  slug?: string;
  full_slug?: string;
  title?: string;
  name?: string;
}

interface SchemaOrgPageOptions {
  template?: PageTemplate | string;
  title?: string;
  description?: string;
  image?: string;
  blocks?: PageBlocks[];
  listItems?: ListItemInput[];
  listItemType?: string;
  entity?: SchemaOrgNode;
}

const entityIdByKind = (kind: SchemaKind, pageUrl: string): string | null => {
  const base = pageUrl.replace(/\/$/, '');

  switch (kind) {
    case 'article':
      return `${base}/#article`;
    case 'offer':
      return `${base}/#offer`;
    case 'service':
      return `${base}/#procedure`;
    case 'doctor':
      return `${base}/#doctor`;
    case 'review':
      return `${base}/#review`;
    case 'news':
      return `${base}/#article`;
    case 'price':
      return `${base}/#offercatalog`;
    case 'collection':
      return `${base}/#itemlist`;
    default:
      return null;
  }
};

const findAuthorBlock = (blocks?: PageBlocks[]): BlockAuthor | undefined => {
  return blocks?.find(block => block.type === 'BlockAuthor') as BlockAuthor | undefined;
};

const buildEntity = (
  kind: SchemaKind,
  options: {
    siteUrl: string;
    pageUrl: string;
    title: string;
    description: string;
    image?: string;
    blocks?: PageBlocks[];
  },
): SchemaOrgNode | null => {
  const { siteUrl, pageUrl, title, description, image, blocks } = options;
  const id = entityIdByKind(kind, pageUrl);
  const orgId = organizationId(siteUrl);

  if (kind === 'article') {
    const author = findAuthorBlock(blocks);
    const node: SchemaOrgNode = {
      '@id': id,
      '@type': 'BlogPosting',
      headline: title,
      url: pageUrl,
      inLanguage: 'ru-RU',
      mainEntityOfPage: { '@id': webpageId(pageUrl) },
      publisher: { '@id': orgId },
      description,
    };

    if (image) {
      node.image = image;
    }

    if (author?.created) {
      node.datePublished = author.created;
    }

    if (author?.updated) {
      node.dateModified = author.updated;
    }

    if (author?.doctor) {
      node.author = {
        '@type': 'Person',
        name: author.doctor.full_name,
        url: absoluteUrl(siteUrl, author.doctor.full_slug || author.doctor.link || ''),
      };
    }

    return node;
  }

  if (kind === 'offer') {
    return {
      '@id': id,
      '@type': 'Offer',
      name: title,
      url: pageUrl,
      seller: { '@id': orgId },
      availability: 'https://schema.org/InStock',
      description,
    };
  }

  if (kind === 'service') {
    return {
      '@id': id,
      '@type': 'MedicalProcedure',
      name: title,
      medicalSpecialty: 'https://schema.org/Dentistry',
      provider: { '@id': orgId },
      description,
    };
  }

  if (kind === 'review') {
    return {
      '@id': id,
      '@type': 'Review',
      name: title,
      reviewBody: description,
      itemReviewed: { '@id': orgId },
    };
  }

  if (kind === 'news') {
    return {
      '@id': id,
      '@type': 'NewsArticle',
      headline: title,
      url: pageUrl,
      description,
      publisher: { '@id': orgId },
    };
  }

  if (kind === 'price') {
    return {
      '@id': id,
      '@type': 'OfferCatalog',
      name: title,
      itemListElement: [],
    };
  }

  return null;
};

const buildItemList = (
  kind: SchemaKind,
  options: {
    siteUrl: string;
    pageUrl: string;
    title: string;
    listItems?: ListItemInput[];
    listItemType?: string;
    path: string;
  },
): SchemaOrgNode | null => {
  if (kind !== 'collection' || !options.listItems?.length) {
    return null;
  }

  const itemType = options.listItemType || collectionItemType(options.path);

  return {
    '@id': entityIdByKind(kind, options.pageUrl),
    '@type': 'ItemList',
    name: options.title,
    itemListElement: options.listItems.map((item, index) => {
      const url = absoluteUrl(options.siteUrl, item.full_slug || item.slug || item.url || '');

      return {
        '@type': 'ListItem',
        position: index + 1,
        url,
        item: {
          '@type': itemType,
          name: item.title || item.name || '',
          url,
        },
      };
    }),
  };
};

export const useSchemaOrgPage = (options: SchemaOrgPageOptions) => {
  const siteUrl = useRuntimeConfig().public.siteUrl as string;
  const path = useRoute().path || '/';
  const kind = resolveSchemaKind(path, options.template);
  const pageUrl = absoluteUrl(siteUrl, path);
  const title = options.title || '';
  const description = options.description || '';
  const nodes: SchemaOrgNode[] = [];

  const entity =
    options.entity ||
    buildEntity(kind, {
      siteUrl,
      pageUrl,
      title,
      description,
      image: options.image,
      blocks: options.blocks,
    });

  const itemList = buildItemList(kind, {
    siteUrl,
    pageUrl,
    title,
    listItems: options.listItems,
    listItemType: options.listItemType,
    path,
  });

  const webpage: SchemaOrgNode = {
    '@id': webpageId(pageUrl),
    '@type': webpageTypeByKind(kind),
    url: pageUrl,
    name: title,
    inLanguage: 'ru-RU',
    isPartOf: { '@id': websiteId(siteUrl) },
    about: { '@id': organizationId(siteUrl) },
    description,
  };

  if (kind === 'about') {
    webpage.mainEntity = { '@id': organizationId(siteUrl) };
  } else if (itemList) {
    webpage.mainEntity = { '@id': itemList['@id'] };
    nodes.push(itemList);
  } else if (entity) {
    webpage.mainEntity = { '@id': entity['@id'] };
    nodes.push(entity);
  }

  useSchemaOrgHead([webpage, ...nodes], 'schema-org-page');
};
